import { useState, useRef } from "react";
import { FiShieldOff } from "react-icons/fi";
import { MfaIconBadge, backupCodeInputClass, otpInputClass } from "./MfaUi";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { authService } from "./mfa.api";
import { useAuth } from "context/auth-context";

type Props = {
  onClose: () => void;
  onSuccess?: () => void;
};

const DisableMfaModal = ({ onClose, onSuccess }: Props) => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [backupCode, setBackupCode] = useState<string>("");
  const [useBackup, setUseBackup] = useState<boolean>(false);
  const { auth } = useAuth();

  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // 🔥 React Query
  const { mutateAsync, isLoading: loading } = useMutation({
    mutationFn: authService.disableMfa,
  });

  // 🔹 OTP input
  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "");

    if (paste.length === 6) {
      setOtp(paste.split(""));
      inputsRef.current[5]?.focus();
    }
  };

  // 🔐 Submit
  const handleVerify = async () => {
    const finalOtp = otp.join("");

    if (!useBackup && finalOtp.length !== 6) {
      return toast.error("Enter valid 6-digit OTP");
    }

    if (useBackup && !backupCode) {
      return toast.error("Enter backup code");
    }

    try {
      const res = await mutateAsync({
        email: auth.email,
        otp: useBackup ? undefined : finalOtp,
        backupCode: useBackup ? backupCode : undefined,
      });
      if (res?.data?.success) {
        toast.success(res?.data?.message || "MFA disabled successfully");
      } else {
        toast.error(res?.data?.message || "Failed to disable MFA");
        return;
      }
      onSuccess?.();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to disable MFA");
    }
  };

  return (
    <div className="zs-backdrop" role="dialog" aria-modal="true" aria-labelledby="mfa-disable-title">
      <div className="zs-modal max-w-md">
        {/* Header */}
        <div className="flex flex-col items-center px-5 pt-6 pb-4 text-center sm:px-6">
          <div className="mb-3">
            <MfaIconBadge tone="danger" size="lg">
              <FiShieldOff />
            </MfaIconBadge>
          </div>

          <h2 id="mfa-disable-title" className="text-lg font-semibold font-display text-ink">
            Disable Multi-Factor Authentication
          </h2>

          <p className="mt-1 text-sm text-ink-3">
            {useBackup
              ? "Enter one of your backup codes"
              : "Enter 6-digit code from your authenticator app"}
          </p>
        </div>

        <div className="flex flex-col gap-4 px-5 pb-5 sm:px-6">
          {/* OTP */}
          {!useBackup ? (
            <div className="flex justify-center w-full gap-2 mx-auto">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={digit}
                  inputMode="numeric"
                  aria-label={`Digit ${index + 1}`}
                  ref={(el) => (inputsRef.current[index] = el)}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  className={otpInputClass}
                />
              ))}
            </div>
          ) : (
            <input
              type="text"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              placeholder="Enter backup code"
              className={backupCodeInputClass}
            />
          )}

          {/* Button */}
          <button onClick={handleVerify} disabled={loading} className="zs-btn zs-btn-danger zs-btn-block">
            {loading ? "Disabling..." : "Disable MFA"}
          </button>

          {/* Toggle */}
          <div className="p-3 text-center border rounded bg-surface-2 border-line">
            <p className="text-sm text-ink-3">Don&apos;t have access to your authenticator?</p>

            <button
              onClick={() => {
                setUseBackup(!useBackup);
                setOtp(new Array(6).fill(""));
                setBackupCode("");
              }}
              className="mt-1 text-sm zs-link"
            >
              {useBackup ? "Use Authenticator Code" : "Use Backup Code"}
            </button>
          </div>

          {/* Footer */}
          <p className="text-xs text-center text-ink-4">Each backup code can be used only once</p>
        </div>

        {/* Cancel */}
        <div className="flex justify-end px-5 py-4 border-t sm:px-6 border-line bg-surface-2 rounded-b-xl">
          <button onClick={onClose} className="w-full zs-btn zs-btn-secondary sm:w-auto">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisableMfaModal;
